import React, { useRef, useEffect, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { stripAllHtmlAndEncodedTags } from "../../utils";

const modules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
};

const formats = ["bold", "italic", "underline", "list", "bullet"];

const TextEditor = ({ inputData, onChange, disabled, name }: any) => {
  const quillRef = useRef<ReactQuill>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Last value this editor pushed up through onChange. When inputData equals
  // it, the prop is just the parent echoing the user's own typing back, so
  // the editor must be left alone (re-pasting would reset the cursor).
  const lastEmitted = useRef<string | null>(null);

  // Sync the editor whenever inputData changes from outside -- on mount, when
  // an async fetch resolves later, or when the parent replaces the value
  // (e.g. selecting a Distribution Rule that carries its own Note while the
  // editor already holds the previous note).
  // setTimeout(0) defers one tick so Quill's clipboard module is fully
  // initialised before we call dangerouslyPasteHTML — without this deferral
  // Quill can insert the raw HTML string as plain text.
  useEffect(() => {
    const timer = setTimeout(() => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      if (!inputData) {
        if (editor.getText().trim() !== "") editor.setText(""); // clears content
        return;
      }
      if (inputData === lastEmitted.current) return;
      if (inputData === editor.root.innerHTML) return;

      if (editor.getText().trim() !== "") editor.setText("");
      editor.clipboard.dangerouslyPasteHTML(0, inputData);
      editor.setSelection(editor.getLength(), 0);
    }, 0);
    return () => clearTimeout(timer);
  }, [inputData]);

  const emitChange = useCallback(
    (content: string) => {
      lastEmitted.current = content;
      onChange(content);
    },
    [onChange],
  );

  const handleChange = useCallback(
    (content: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        emitChange(content);
      }, 300);
    },
    [emitChange],
  );

  const handleBlur = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    emitChange(
      editor.root.innerHTML === "<p><br></p>" ? "" : editor.root.innerHTML,
    );
  }, [emitChange]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const html =
      clipboardData.getData("text/html") || clipboardData.getData("text/plain");
    const cleanedHtml = stripAllHtmlAndEncodedTags(html);

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    editor.clipboard.dangerouslyPasteHTML(
      range?.index ?? editor.getLength(),
      cleanedHtml,
    );
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  return (
    <div
      onPaste={disabled ? undefined : handlePaste}
      onBlur={handleBlur}
      style={{
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={modules}
        formats={formats}
        readOnly={disabled}
        onChange={handleChange}
      />
    </div>
  );
};

export default TextEditor;
